import {
  DSCheckbox,
  DSInput,
} from "@components/design-system/molecules";
import React, { useEffect } from "react";
import {
  Controller,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { EmailExportTypeForm } from "./EmailExportTypeForm";
import HeadingWrapper from "../common/HeadingWrapper";
import { ExportNoteInline } from "../notes/ExportNoteInline";

export default function ExportForm({
  control,
  isClientSummaryMode,
  isClientSummaryModalOpen,
  methods,
  clientSummary,
  getValues,
  note,
}) {
  const {
    formState: { errors },
  } = useFormContext();

  const { setValue } = methods;

  const exportType = useWatch({ name: "exportType" });

  // If we are in client summary mode, we should reset the email whenever the modal is closed
  useEffect(() => {
    if (!isClientSummaryModalOpen) {
      setValue("toEmail", "");
    }
  }, [isClientSummaryModalOpen, setValue]);

  return (
    <>
      {isClientSummaryMode && (
        <>
          <ExportNoteInline
            isClientSummaryMode={true}
            clientSummary={clientSummary}
            getValues={getValues}
            note={note}
          />
        </>
      )}
      <HeadingWrapper heading={<h4>Send Email</h4>} />
      <div className="space-y-2">
        {!isClientSummaryMode && (
          <EmailExportTypeForm control={control} />
        )}
        <h5>
          Enter the email address (or addresses, separated by commas)
          you would like to send this note to:
        </h5>
        <Controller
          control={control}
          rules={{
            required: "Please enter a recipient email address.",
            validate: (value) => {
              const emails = value.split(",").map((e) => e.trim());
              const emailPattern = /\S+@\S+\.\S+/;
              const invalidEmails = emails.filter(
                (email) => email && !emailPattern.test(email),
              );
              if (invalidEmails.length > 0) {
                return "Please enter valid email address(es).";
              }
              return true;
            },
          }}
          render={({ field: { onChange, value } }) => (
            <DSInput
              placeholder="e.g. doctor@someclinic.com, staff@someclinic.com"
              onChange={(e) => onChange(e)}
              value={value}
              error={errors.toEmail?.message}
              dataTestId="email-export-to-email"
            />
          )}
          name="toEmail"
          defaultValue=""
        />
      </div>

      {!isClientSummaryMode && (
        <div className="space-y-2 pt-8">
          <h5>
            Enter any special instructions for the recipient{" "}
            <small>(optional)</small>:
          </h5>
          <Controller
            control={control}
            rules={{
              required: false,
            }}
            render={({ field: { onChange, value } }) => (
              <DSInput
                placeholder="e.g. Please see below for the medical record we discussed."
                onChange={(e) => onChange(e)}
                value={value}
                dataTestId="email-export-special-instructions"
              />
            )}
            name="specialInstructions"
            defaultValue=""
          />
        </div>
      )}

      {exportType !== "embeddedText" && !isClientSummaryMode && (
        <div className="flex flex-row items-center space-x-sm mt-8">
          <Controller
            control={control}
            rules={{
              required: false,
            }}
            render={({ field: { onChange, value } }) => (
              <DSCheckbox
                onChange={(e) => onChange(e)}
                checked={value}
                disabled={exportType === "embeddedText"}
                aria-label="Include attachments"
                dataTestId="email-export-include-attachments"
              />
            )}
            name="includeAttachments"
            defaultValue={true}
          />
          <h4>Include attachments?</h4>
        </div>
      )}
    </>
  );
}
